import React, { useEffect, useRef, useState } from "react";
import { Bot, X, Send, MessageSquare } from "lucide-react";
import { API_BASE } from "../lib/api";
import { useSiteSettings } from "../lib/hooks";
import { buildWhatsAppLink } from "../lib/format";
import { Link } from "react-router-dom";
import api from "../lib/api";

const linkifySlugs = (text) => {
  // Turn (view: /cars/slug) into a proper link
  const re = /\(view:\s*\/cars\/([a-z0-9-]+)\)/g;
  const parts = [];
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <Link key={m.index} to={`/cars/${m[1]}`} className="text-[#7A1519] underline hover:opacity-80">
        View vehicle →
      </Link>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const sessionId = () => {
  let sid = localStorage.getItem("autobahn_chat_sid");
  if (!sid) {
    sid = "sess-" + Math.random().toString(36).slice(2) + "-" + Date.now();
    localStorage.setItem("autobahn_chat_sid", sid);
  }
  return sid;
};

const AIChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [welcome, setWelcome] = useState("");
  const scrollRef = useRef(null);
  const s = useSiteSettings();

  useEffect(() => {
    api.get("/ai-settings").then((r) => {
      setEnabled(!!r.data?.enabled);
      setWelcome(r.data?.welcome_message || "Hello — how can I help?");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (open && messages.length === 0 && welcome) {
      setMessages([{ role: "assistant", content: welcome }]);
    }
  }, [open, welcome, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: "user", content: input.trim() };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput("");
    setLoading(true);

    // Placeholder assistant message that we'll fill via streaming
    setMessages((m) => [...m, { role: "assistant", content: "" }]);

    try {
      const resp = await fetch(`${API_BASE}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId(), messages: nextMessages }),
      });
      if (!resp.ok || !resp.body) throw new Error("Chat failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const ev of events) {
          const line = ev.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const parsed = JSON.parse(line.slice(5).trim());
            if (parsed.type === "delta" && parsed.content) {
              acc += parsed.content;
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: acc };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry — the assistant is unavailable right now. Please reach us on WhatsApp." };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  const waHref = s?.whatsapp
    ? buildWhatsAppLink(
        s.whatsapp,
        "Hi Autobahn Cars, I was chatting with your website assistant and would like to speak with an advisor."
      )
    : null;

  if (!enabled) return null;

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-40 bg-[#7A1519] hover:bg-[#5a0f12] transition-colors text-white h-14 w-14 rounded-full flex items-center justify-center shadow-xl"
        aria-label="Open chat"
        data-testid="ai-chat-toggle"
      >
        {open ? <X size={24} /> : <Bot size={24} />}
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-6 z-40 w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] max-h-[80vh] bg-white border hairline shadow-2xl rounded-sm flex flex-col"
          data-testid="ai-chat-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b hairline bg-[#111111] text-white">
            <div className="flex items-center gap-2">
              <Bot size={16} className="text-[#7A1519]" />
              <div className="font-semibold text-sm">Autobahn Assistant</div>
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-white/60">Live inventory</div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto chat-scroll p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${i}`}>
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-sm text-sm whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#7A1519] text-white"
                      : "bg-[#F7F6F3] text-[#242424] border hairline"
                  }`}
                >
                  {m.role === "assistant" ? linkifySlugs(m.content) : m.content}
                </div>
              </div>
            ))}
            {loading && messages[messages.length - 1]?.content === "" && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="bg-[#F7F6F3] border hairline px-4 py-2.5 rounded-sm text-sm text-[#242424]/60">…</div>
              </div>
            )}
          </div>

          {waHref && (
            <a
              href={waHref}
              target="_blank"
              rel="noreferrer"
              className="mx-4 mb-2 border hairline text-[#242424] text-xs flex items-center justify-center gap-2 py-2 hover:bg-[#F7F6F3]"
              data-testid="chat-whatsapp-handoff"
            >
              <MessageSquare size={14} className="text-[#25D366]" />
              Chat with Autobahn Cars on WhatsApp
            </a>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t hairline p-3 flex items-center gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about our cars…"
              className="flex-1 bg-[#F7F6F3] rounded-sm px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-[#7A1519]"
              disabled={loading}
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="bg-[#7A1519] disabled:opacity-50 text-white h-10 w-10 flex items-center justify-center rounded-sm hover:bg-[#5a0f12]"
              data-testid="chat-send"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
